import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Meta } from '@df/core/meta/meta';
import { Multisite } from '@df/core/multisite/multisite.interface';
import { ISizeName } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { MultisiteService } from '@df/core/service/multisite';
import { DfModal } from '@df/ui/modal/df-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MultisiteRedirectService } from '@df/ui/multisite/multisite-redirect.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import uniqBy from 'lodash-es/uniqBy';
import { merge } from 'rxjs';
import { NModalLayout } from '../../common/modal/layout/modal-layout.component';
import { NMultisiteSwitchModal } from './multisite-switch-modal.interfaces';

export const storeOptionsFn = () =>
    (window['app'].multisite as MultisiteService).all.map(i => ({
        key: i.siteId,
        title: i.name
    }));

@DfModal({
    id: NMultisiteSwitchModal.componentId,
    position: 'custom',
    config: (sizeId: ISizeName) => ({
        ...(sizeId === 'S' ? NModalLayout.Bottom.baseModalConfig : NModalLayout.Right.baseModalConfig),
        modalClass: 'p-a-0',
        width: sizeId === 'S' ? '100vw' : '500px'
    }),
    blockGroup: 'modals',
    sizeRestriction: sizeId => ({ S: ['S'], M: ['M', 'L', 'X'], L: ['M', 'L', 'X'], X: ['M', 'L', 'X'] })[sizeId],
    priority: 30,
    reopenPolicy: NDfModal.ReopenPolicy.first
})
@ICmsComponent<MultisiteSwitchModalComponent>({
    name: 'Multisite Switch Popup',
    description: 'Appears when user wants to change store.',
    id: NMultisiteSwitchModal.componentId,
    block: {
        url: NMultisiteSwitchModal.componentId,
        scope: 'website'
    },
    onDemandOnly: true,
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'subtitle',
            title: 'Subtitle',
            required: false
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'closeBtnCopy',
            title: 'Close button copy',
            required: false
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'stores',
            title: 'Store links',
            itemName: 'Store link',
            required: true,
            defaultSize: 3,
            fields: [
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'siteId',
                    title: 'Website',
                    description: 'Please select website to link to. If website is off the link will be skipped.',
                    options: storeOptionsFn,
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'icon',
                    title: 'Icon',
                    required: false
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Title',
                    required: true
                }
            ]
        }
    ]
})
@Component({
    selector: 'multisite-switch-modal',
    templateUrl: './multisite-switch-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultisiteSwitchModalComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NMultisiteSwitchModal.IBlockDataContent;

    /**
     * Currently selected store
     */
    protected selectedStore?: string;

    constructor(
        private multisiteRedirectService: MultisiteRedirectService,
        private meta: Meta,
        private events: Events,
        private multisiteService: MultisiteService,
        private modalRef: ModalRef<any>
    ) {
        super();
    }

    /**
     * List of stores
     * only enabled ones are allowed
     */
    protected get stores() {
        return uniqBy(this.content?.stores.filter(i => !!this.multisiteService.toMultisiteItem(i.siteId as string)) || [], 'siteId');
    }

    /**
     * Subscribe to metadata changes to update the links
     */
    override ngOnInit() {
        this.subscriptions.add(
            merge(this.events.on(Multisite.META_ALTERNATE_UPDATE_EVENT), this.meta.subject).subscribe(() => this.detectChanges())
        );
        super.ngOnInit();
    }

    /**
     * Retrieves store url
     */
    protected getStoreUrl(store: NMultisiteSwitchModal.IBlockContentStore): string {
        return this.multisiteRedirectService.getLink(store.siteId as string) || '';
    }

    /**
     * Changes location using multisite service
     * what keeps current path (or uses alternate) and saves website preferrence
     */
    protected goTo(siteId: number | string, $event?: Event) {
        if (this.multisiteRedirectService.to(siteId)) {
            $event?.preventDefault();
        } else {
            this.close();
        }
    }

    /**
     * Closes the modal
     */
    protected close() {
        this.modalRef.close();
    }
}
